import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, X } from 'lucide-react';
import {
  Badge,
  Button,
  FormField,
  IconButton,
  Input,
  Textarea,
} from '@ntconsult/aluma-react';
import type { UploadMetadata } from '../../hooks/useUpload';

interface UploadMetadataFormProps {
  file: globalThis.File;
  onSubmit: (metadata: UploadMetadata) => void;
  onCancel: () => void;
}

export function UploadMetadataForm({ file, onSubmit, onCancel }: UploadMetadataFormProps) {
  const { t } = useTranslation('files');

  const [title, setTitle] = useState(file.name.replace(/\.[^/.]+$/, ''));
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  const addTag = useCallback(() => {
    const value = tagInput.trim().replace(/,$/, '');
    if (value && !tags.includes(value)) {
      setTags((prev) => [...prev, value]);
    }
    setTagInput('');
  }, [tagInput, tags]);

  const handleTagKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        addTag();
      }
    },
    [addTag],
  );

  const removeTag = useCallback((tagToRemove: string) => {
    setTags((prev) => prev.filter((t) => t !== tagToRemove));
  }, []);

  const handleSubmit = () => {
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      tags: tags.length > 0 ? tags : undefined,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
        <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
        <span className="text-sm text-foreground truncate flex-1">{file.name}</span>
        <IconButton
          variant="ghost"
          size="xs"
          onClick={onCancel}
          label={t('changeFile')}
          icon={<X className="w-4 h-4" />}
        />
      </div>

      <FormField label={t('fieldTitle')} required>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </FormField>

      <FormField label={t('fieldDescription')}>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          resize="none"
        />
      </FormField>

      <FormField label={t('fieldTags')}>
        <div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {tags.map((tag) => (
                <Badge key={tag} variant="soft" tone="brand" size="sm">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    aria-label={t('removeTag', { defaultValue: 'Remover tag', tag })}
                    className="-mr-1 ml-0.5 inline-flex items-center justify-center rounded-sm hover:bg-brand-primary/15 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-focus"
                  >
                    <X className="w-3 h-3" aria-hidden="true" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          <Input
            type="text"
            size="sm"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            onBlur={addTag}
            placeholder={t('tagPlaceholder')}
          />
        </div>
      </FormField>

      <div className="flex justify-end gap-2 pt-2 border-t border-border">
        <Button variant="ghost" onClick={onCancel}>
          {t('cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={!title.trim()}>
          {t('save')}
        </Button>
      </div>
    </div>
  );
}
